import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  trackOnboardingError,
  trackOnboardingEvent,
} from "../analytics/onboarding";
import { trackFinancialError, trackFinancialEvent } from "../analytics/financial";
import type { FormChatState } from "../repositories/formChat.repository";
import {
  buildBankAccountCreatePayload,
  buildBankAccountUpdatePayload,
} from "../utils/bankAccountFormChatPayloadBuilder";
import { notifyLimitReached } from "../utils/limitToasts";
import { readReturnPath } from "../utils/navigationOrigin";
import { useBankAccountLimit } from "./useBankAccountLimit";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useCreateBankAccountMutation } from "./useCreateBankAccountMutation";
import { useFeatureLimit } from "./useFeatureLimit";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useUpdateBankAccountMutation } from "./useUpdateBankAccountMutation";

const SOURCE_PAGE = "FormChatAssistant";

export class BankAccountChatSubmitError extends Error {}

export function useFormChatSubmitBankAccount({
  editTargetId,
  enabled = true,
}: {
  editTargetId?: string;
  enabled?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const productBase = useProductAnalyticsBase();
  const terreiroId = useCachedTerreiro()?.id;
  const mediumId = useCachedMedium()?.id;
  const isEdit = Boolean(editTargetId);

  const { canAddBankAccount, isLoading: isEnhancedLoading } =
    useBankAccountLimit();
  const legacyLimit = useFeatureLimit({
    feature: "maxBankAccounts",
    enabled: enabled && !isEdit,
  });
  const canCreate =
    canAddBankAccount !== undefined ? canAddBankAccount : legacyLimit.canCreate;
  const createMutation = useCreateBankAccountMutation();
  const updateMutation = useUpdateBankAccountMutation();

  const navigateBack = useCallback(() => {
    navigate(readReturnPath(location.state) ?? "/financial");
  }, [location.state, navigate]);

  const submitBankAccountByChat = useCallback(
    async (state: FormChatState) => {
      const analyticsBase = { ...productBase, trigger: "mutation" as const };
      const eventSource = { source: "form_chat" };

      if (isEdit && editTargetId) {
        const payload = buildBankAccountUpdatePayload(state);
        if (!payload) {
          throw new BankAccountChatSubmitError(
            "Revise os dados da conta no chat antes de confirmar.",
          );
        }
        const eventProps = { account_id: editTargetId, ...eventSource };
        trackFinancialEvent(
          "financial_account_update_submit_attempt",
          analyticsBase,
          eventProps,
        );
        try {
          await updateMutation.mutateAsync({ id: editTargetId, payload });
        } catch (error) {
          trackFinancialError(
            "financial_account_update_error",
            analyticsBase,
            error,
            eventProps,
          );
          throw error;
        }
        trackFinancialEvent("financial_account_updated", analyticsBase, eventProps);
        navigateBack();
        return;
      }

      const onboardingBase = {
        sourcePage: SOURCE_PAGE,
        terreiroId,
        mediumId,
        trigger: "mutation" as const,
      };
      if (!canCreate) {
        trackOnboardingEvent("onboarding_bank_account_created", {
          ...onboardingBase,
          success: false,
          featureLimitBlocked: true,
        });
        trackFinancialEvent(
          "financial_account_create_submit_attempt",
          { ...analyticsBase, success: false, featureLimitBlocked: true },
          eventSource,
        );
        notifyLimitReached({
          feature: "maxBankAccounts",
          message: legacyLimit.message,
        });
        throw new BankAccountChatSubmitError(
          legacyLimit.message || "Limite de contas bancárias atingido.",
        );
      }

      const payload = buildBankAccountCreatePayload(state, { terreiroId, mediumId });
      if (!payload) {
        throw new BankAccountChatSubmitError(
          "Revise os dados da conta no chat antes de confirmar.",
        );
      }
      const scopeProps = {
        account_type: payload.accountType,
        owner_scope: terreiroId ? "terreiro" : "personal",
        ...eventSource,
      };
      trackFinancialEvent("financial_account_create_submit_attempt", analyticsBase, {
        account_type: payload.accountType,
        ...eventSource,
      });
      try {
        await createMutation.mutateAsync(payload);
      } catch (error) {
        trackOnboardingError(
          "onboarding_bank_account_created",
          onboardingBase,
          error,
          scopeProps,
        );
        trackFinancialError("financial_account_create_error", analyticsBase, error, {
          account_type: payload.accountType,
          ...eventSource,
        });
        throw error;
      }
      trackOnboardingEvent("onboarding_bank_account_created", onboardingBase, scopeProps);
      trackFinancialEvent("financial_account_created", analyticsBase, scopeProps);
      await legacyLimit.invalidate();
      navigateBack();
    },
    [
      canCreate,
      createMutation,
      editTargetId,
      isEdit,
      legacyLimit,
      mediumId,
      navigateBack,
      productBase,
      terreiroId,
      updateMutation,
    ],
  );

  return {
    submitBankAccountByChat,
    navigateBack,
    canCreate: isEdit || canCreate,
    isLimitLoading: !isEdit && (isEnhancedLoading || legacyLimit.isLoading),
    limitMessage: legacyLimit.message,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}
